/**
 * Visibilidad REAL de un ancla del tour.
 *
 * Existir en el DOM no es lo mismo que poder señalarse, y confundir las dos cosas fue la causa de
 * que el tour dejara la pantalla cubierta por su velo sin poder tocar nada:
 *
 *  - `querySelector` encuentra elementos que están ocultos.
 *  - El rect NO sirve por sí solo: MUI esconde un panel deslizante con `translateX(-100%)` y
 *    tamaño REAL, así que mide algo aunque esté fuera de la pantalla.
 *  - `offsetParent` tampoco: solo es `null` con `display: none`, y MUI oculta con
 *    `visibility: hidden`.
 *
 * El único predicado fiable es recorrer los ancestros mirando el estilo computado, que es
 * EXACTAMENTE lo que hace `react-joyride` por dentro. Copiar su criterio garantiza que nuestra
 * decisión y la suya nunca discrepen, que es de donde salían los velos huérfanos.
 */

// Anclas que existen fuera del menú y por tanto NO prueban que el menú esté abierto: la
// bienvenida se centra en `body`, y la foto que abre el menú vive en la barra inferior.
const ALWAYS_PRESENT_TARGETS = ['body', '[data-tour="menu-toggle"]'];

/** ¿El elemento y toda su cadena de ancestros están visibles? */
const isChainVisible = (element) => {
  let node = element;

  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return false;
    }
    node = node.parentElement;
  }

  return true;
};

/**
 * ¿Se puede señalar este ancla ahora mismo?
 *
 * `body` siempre vale: es el ancla de los pasos centrados y no puede desaparecer.
 */
export const isTourTargetVisible = (target) => {
  if (!target) return false;
  if (target === 'body') return true;
  if (typeof document === 'undefined' || typeof window === 'undefined') return false;

  const el = document.querySelector(target);
  if (!el || !isChainVisible(el)) return false;

  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return false;

  // Debe INTERSECAR la ventana. Esto atrapa el panel a medio animar: ya no está oculto, pero
  // sigue fuera de pantalla y un tooltip apuntando ahí queda donde nadie lo ve.
  return rect.right > 0 && rect.bottom > 0 && rect.left < window.innerWidth && rect.top < window.innerHeight;
};

/** Los pasos que se pueden señalar ahora mismo. */
export const visibleTourSteps = (steps) => steps.filter((step) => isTourTargetVisible(step.target));

/**
 * ¿Queda al menos un paso anclado al MENÚ?
 *
 * Sin esta comprobación el tour arrancaba igual en móvil con el menú cerrado: siempre sobreviven
 * la bienvenida (anclada a `body`) y el paso de la foto (que está en la barra inferior), así que
 * contar los pasos a secas nunca daba cero y la guarda no protegía de nada.
 */
export const hasMenuAnchoredSteps = (steps) =>
  steps.some((step) => !ALWAYS_PRESENT_TARGETS.includes(step.target));
